<script setup lang="ts">
import type { ArticleRecord } from '~/types/datocms'
import { CardType, AppTheme } from '~/types'
import { useAppStore } from '~/stores/AppStore'

interface CardProps {
  data: ArticleRecord
  type?: CardType
}

withDefaults(defineProps<CardProps>(), {
  type: CardType.NEWS
})

const store = useAppStore()
const borderColor = computed(() =>
  store.theme === AppTheme.DARK ? 'border-white' : 'border-black'
)
</script>

<template>
  <div
    :class="[
      borderColor,
      {
        'lg:flex lg:h-full lg:gap-5': type === CardType.NEWS,
        'lg:mb-5 lg:h-[calc(100%-1.5rem)]': type === CardType.NEWS_EXPOSED,
        'lg:mb-4 lg:h-[calc(100%-1rem)]': type === CardType.NEWS_LATEST
      }
    ]"
    class="group block border-b-2 border-l-2 border-r-0 border-t-0 lg:border-b-0 lg:border-l-0 lg:border-r-2 lg:border-t-2"
  >
    <!--  
    <span
      class="absolute left-0 top-0 z-[1] block h-0 w-full -translate-x-full border-b-2 transition-transform duration-[600ms] ease-quart-out group-hover:translate-x-0"
      :class="[borderColor]"
    ></span>
    <span
      class="z-[1]block absolute right-0 top-0 h-full w-0 -translate-y-full border-r-2 transition-transform duration-[600ms] ease-quart-out group-hover:-translate-y-0"
      :class="[borderColor]"
    ></span>
    <span
      class="absolute bottom-0 left-0 z-[1] block h-0 w-full border-b-2 transition-transform duration-[600ms] ease-quart-out group-hover:-translate-x-full"
      :class="[borderColor]"
    ></span>
    <span
      class="absolute left-0 top-0 z-[1] block h-full w-0 border-l-2 transition-transform duration-[600ms] ease-quart-out group-hover:-translate-y-full"
      :class="[borderColor]"
    ></span>
    -->
    <NuxtLink
      :to="usePathByType(data._modelApiKey, (data.slug as string) ?? undefined)"
      class="block overflow-hidden border-b-2 lg:border-b-0"
      :class="[
        borderColor,
        {
          'lg:aspect-16/10 lg:w-full lg:max-w-[31%] lg:flex-1': type === CardType.NEWS
        }
      ]"
    >
      <Image
        v-if="data.image"
        class="aspect-4/3 w-full object-cover transition-transform duration-400 ease-out group-hover:scale-105"
        :class="{
          'lg:aspect-cinema': type === CardType.NEWS_EXPOSED,
          'lg:aspect-video': type === CardType.NEWS_LATEST,
          'lg:aspect-16/10': type === CardType.NEWS
        }"
        loading="lazy"
        :src="data.image.responsiveImage ? data.image.responsiveImage?.src : data.image.url"
        :srcset="data.image.responsiveImage ? data.image.responsiveImage?.webpSrcSet : undefined"
        :width="
          data.image.responsiveImage
            ? data?.image.responsiveImage?.width
            : (data.image.width ?? 528)
        "
        :height="
          data.image.responsiveImage
            ? data.image.responsiveImage?.height
            : (data.image.height ?? 396)
        "
        sizes="auto"
        :alt="data.image.alt ?? ''"
      />
    </NuxtLink>
    <div
      class="flex flex-col gap-1 px-4 py-3 leading-tight lg:gap-4 lg:pb-0 lg:pl-0 lg:pt-3.5"
      :class="[
        {
          'flex-1 lg:flex lg:h-full lg:flex-col lg:justify-between': type === CardType.NEWS
        }
      ]"
    >
      <div
        class="font-bold lg:text-2xl-sb"
        :class="type === CardType.NEWS_EXPOSED ? 'lg:text-3xl-sb' : 'lg:text-2xl-sb'"
      >
        <NuxtLink :to="usePathByType(data._modelApiKey, (data.slug as string) ?? undefined)">{{
          data.title
        }}</NuxtLink>
      </div>
      <div class="flex gap-2">
        <NuxtLink
          v-for="(category, idx) in data.category"
          :key="category.id"
          :to="`/news?category=${category.slug}`"
          class="inline-block text-sm lg:text-base lg:uppercase"
          >{{ category.name }}<span v-if="idx < data.category.length - 1">,</span></NuxtLink
        >
      </div>
    </div>
  </div>
</template>
